import React, { useEffect, useState } from "react";
import { Drawer, Box, Grid, TextField, Typography, IconButton, Button as MuiButton, CircularProgress, InputAdornment, Switch, FormControlLabel } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationInstrumentadorSchema } from "../../../schemas/validationSchema.ts";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";
import instrumentadorService from "../../../services/instrumentadorService.ts";
import enderecoService from "../../../services/enderecoService.ts";
import setupService from "../../../services/setupService.ts";
import { Instrumentador } from "../../../models/Instrumentador.ts";
import { Endereco } from "../../../models/Endereco.ts";
import { formatBrazilPhone } from "../../../utils/phone.util.ts";
import { maskCep } from "../../../utils/address.util.ts";
import { maskCpf } from "../../../utils/cpf.util.ts";

interface CreateInstrumentadorDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (r: any) => void;
}

// Mesma receita de cores dos demais módulos — sem ThemeProvider do MUI,
// estados de label/borda caem no tema claro padrão do MUI, sem contraste
// no fundo escuro. CSS vars do design system em vez de tokens de tema.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
  },
  "& .MuiFormHelperText-root": { color: "var(--red)" },
};

const scrollSx = {
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--border-strong)", borderRadius: 4 },
};

const CreateInstrumentadorDrawer: React.FC<CreateInstrumentadorDrawerProps> = ({ open, onClose, onCreated }) => {
  const { showFeedback } = useFeedback();
  const [isLoading, setIsLoading] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [podeNotificar, setPodeNotificar] = useState(false);

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const features = await setupService.listFeatures();
        const funcionalidades = features.data.funcionalidade || {};
        setPodeNotificar(!!funcionalidades.notificarEmail);
      } catch (error) {
        console.error("Erro ao buscar funcionalidades do plano:", error);
      }
    };
    fetchFeatures();
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationInstrumentadorSchema),
    defaultValues: { notificarEmail: false },
  });

  const handleCepChange = async (value: string) => {
    const masked = maskCep(value);
    setValue("endereco.cep", masked, { shouldValidate: true });
    const raw = masked.replace(/\D/g, "");
    if (raw.length === 8) {
      setBuscandoCep(true);
      await enderecoService.getAddressByZipCode(raw, "endereco", setValue, showFeedback);
      setBuscandoCep(false);
    }
  };

  const handleClose = () => {
    reset({ notificarEmail: false });
    onClose();
  };

  const onSubmit = async (data: any) => {
    const endereco = new Endereco(
      data.endereco?.rua,
      data.endereco?.bairro,
      data.endereco?.cep,
      data.endereco?.numero,
      data.endereco?.cidade,
      data.endereco?.uf
    );
    // id vai 0 aqui só porque o construtor exige um valor — o service de
    // criação remove o campo id do payload antes de enviar (ver instrumentadorService.create).
    const novoInstrumentador = new Instrumentador(0, data.nome, data.cpf, data.email, data.telefone, data.notificarEmail, endereco);

    try {
      setIsLoading(true);
      const resp = await instrumentadorService.create(novoInstrumentador);
      showFeedback(resp.message, "success");
      onCreated?.(resp);
      reset({ notificarEmail: false });
    } catch (err: any) {
      showFeedback(err.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          bgcolor: "var(--surface)",
          color: "var(--ink)",
          borderLeft: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Cabeçalho fixo */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid var(--border)", flex: "0 0 auto" }}>
        <div>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Novo Instrumentador</Typography>
          <Typography variant="body2" sx={{ color: "var(--ink-faint)", mt: 0.25 }}>Cadastre um novo instrumentador</Typography>
        </div>
        <IconButton onClick={handleClose} size="small" sx={{ color: "var(--ink-faint)" }} aria-label="Fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Corpo com scroll — flex:1 + overflowY:auto, o resto do Drawer fica fixo */}
      <Box
        component="form"
        id="create-instrumentador-form"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, ...scrollSx }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nome do Instrumentador*" size="small" fullWidth sx={fieldSx}
              {...register("nome")} autoComplete="off" error={!!errors.nome} helperText={errors.nome?.message as string}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="CPF*" size="small" fullWidth sx={fieldSx}
              value={watch("cpf") || ""}
              onChange={(e) => setValue("cpf", maskCpf(e.target.value), { shouldValidate: true })}
              error={!!errors.cpf} helperText={errors.cpf?.message as string}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Telefone*" size="small" fullWidth sx={fieldSx}
              value={watch("telefone") || ""}
              onChange={(e) => setValue("telefone", formatBrazilPhone(e.target.value), { shouldValidate: true })}
              error={!!errors.telefone} helperText={errors.telefone?.message as string}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="E-mail" size="small" fullWidth sx={fieldSx}
              {...register("email")} error={!!errors.email} helperText={errors.email?.message as string}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!watch("notificarEmail")}
                  onChange={(e) => setValue("notificarEmail", e.target.checked, { shouldValidate: true })}
                  disabled={!podeNotificar}
                />
              }
              label="Notificar por e-mail"
              sx={{ color: "var(--ink)" }}
            />
            {!podeNotificar && (
              <Typography variant="caption" sx={{ color: "var(--red)", display: "block" }}>
                Disponível apenas para planos Premium.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--ink)" }}>
              Endereço
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="CEP" size="small" fullWidth sx={fieldSx}
              value={watch("endereco.cep") || ""}
              onChange={(e) => handleCepChange(e.target.value)}
              disabled={buscandoCep}
              error={!!(errors.endereco as any)?.cep}
              helperText={(errors.endereco as any)?.cep?.message}
              InputProps={{ endAdornment: buscandoCep ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : undefined }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Bairro" size="small" fullWidth sx={fieldSx} {...register("endereco.bairro")} />
          </Grid>

          <Grid item xs={6}>
            <TextField label="Cidade" size="small" fullWidth sx={fieldSx} {...register("endereco.cidade")} />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="UF" size="small" fullWidth sx={fieldSx} {...register("endereco.uf")}
              inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
            />
          </Grid>

          <Grid item xs={8}>
            <TextField label="Rua" size="small" fullWidth sx={fieldSx} {...register("endereco.rua")} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Número" size="small" fullWidth sx={fieldSx} {...register("endereco.numero")} />
          </Grid>
        </Grid>
      </Box>

      {/* Rodapé fixo */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 3, py: 2.5, borderTop: "1px solid var(--border)", flex: "0 0 auto" }}>
        <MuiButton onClick={handleClose} disabled={isLoading} sx={{ color: "var(--ink-soft)" }}>
          Cancelar
        </MuiButton>
        <MuiButton type="submit" form="create-instrumentador-form" variant="contained" disabled={isLoading} disableElevation>
          {isLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Cadastrar"}
        </MuiButton>
      </Box>
    </Drawer>
  );
};

export default CreateInstrumentadorDrawer;
